// The whole demo in one drop: company, people and sessions from a single folder.
//
// Two things make it worth being one control rather than two uploads. The sessions need the user
// ids, which only exist once the people are created — doing it in one run means the manifest can
// name owners by username and the screen resolves them. And a re-import has to be safe: people
// that already exist are skipped, and a session whose start second already exists is updated in
// place rather than colliding with itself. That is what makes "regenerate one file, drop the
// folder again" the normal way to work.
import React, { useMemo, useRef, useState } from "react";
import { Button } from "common-tailwind-components";
import { CheckCircle2, CircleDashed, FolderOpen, RefreshCw, Upload, XCircle } from "lucide-react";
import clsx from "clsx";
import { useTranslation } from "react-i18next";

import {
  admin_create_recording,
  admin_list_seeded_user_ids,
  admin_patch_recording,
  create_company,
  create_user,
  formatApiRequestError,
  get_all_recordings,
  get_companies,
  get_users,
  set_user_permissions,
} from "../../api";
import type { User } from "../../api/types";
import {
  DEMO_COMPANY_ID_MAX,
  DEMO_COMPANY_ID_MIN,
  MANIFEST_NAME,
  analysisStartTimestamp,
  filesFromDrop,
  generatePassword,
  parseManifest,
  sessionKey,
  type DemoManifest,
} from "./demoBundle";

/** Shown once: the password is generated here and stored nowhere readable. */
type SeededCredential = { user_id: string; username: string; password: string };

/** In flight at once. Enough to hide the round trips, few enough to keep the router unbothered. */
const UPLOAD_CONCURRENCY = 3;

type RowStatus = "pending" | "uploading" | "created" | "updated" | "failed";

const STATUS_MARKS: Record<RowStatus, [typeof CheckCircle2, string]> = {
  pending: [CircleDashed, "text-text-dim"],
  uploading: [CircleDashed, "text-text-dim"],
  created: [CheckCircle2, "text-success"],
  updated: [RefreshCw, "text-info"],
  failed: [XCircle, "text-danger"],
};

type SessionRow = {
  file: File;
  ownerName: string;
  name: string;
  includeInProcesses: boolean;
  status: RowStatus;
  error: string | null;
};

type Bundle = {
  manifest: DemoManifest;
  rows: SessionRow[];
  missingFiles: string[];
  warnings: string[];
};

const DemoImport = ({ onImported }: { onImported: () => void }) => {
  const { t } = useTranslation();
  const [bundle, setBundle] = useState<Bundle | null>(null);
  const [parseError, setParseError] = useState<string | null>(null);
  const [runError, setRunError] = useState<string | null>(null);
  const [credentials, setCredentials] = useState<SeededCredential[] | null>(null);
  const [skippedUsers, setSkippedUsers] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [dragging, setDragging] = useState(false);
  const folderInput = useRef<HTMLInputElement>(null);

  const counts = useMemo(() => {
    const rows = bundle?.rows ?? [];
    return {
      total: rows.length,
      done: rows.filter(row => row.status === "created" || row.status === "updated").length,
      failed: rows.filter(row => row.status === "failed").length,
    };
  }, [bundle]);

  const acceptFiles = async (files: File[]) => {
    setParseError(null);
    setRunError(null);
    setCredentials(null);
    setSkippedUsers([]);
    setBundle(null);
    if (files.length === 0) return;

    const byName = new Map(files.map(file => [file.name, file]));
    const manifestFile = byName.get(MANIFEST_NAME);
    if (!manifestFile) {
      setParseError(t("tour.demo_manifest_missing", { name: MANIFEST_NAME }));
      return;
    }

    let manifest: DemoManifest;
    try {
      manifest = parseManifest(await manifestFile.text());
    } catch (err: unknown) {
      setParseError(err instanceof Error ? err.message : t("tour.demo_manifest_unreadable"));
      return;
    }

    const missingFiles: string[] = [];
    const rows: SessionRow[] = [];
    for (const entry of manifest.recordings) {
      const file = byName.get(entry.file);
      if (!file) {
        missingFiles.push(entry.file);
        continue;
      }
      rows.push({
        file,
        ownerName: entry.owner?.trim() ?? "",
        // A nameless entry stays importable: the endpoint requires a name, and falling back to the
        // filename beats rejecting the row for something the manifest merely left out.
        name: entry.name?.trim() || file.name.replace(/\.json$/i, ""),
        includeInProcesses: entry.include_in_processes ?? true,
        status: "pending",
        error: null,
      });
    }

    const warnings: string[] = [];
    // Only worth saying when the company is about to be created: a manifest that adds sessions to
    // people who already exist names their real company, which is not expected to be in the band.
    const createsUsers = manifest.users.length > 0;
    if (
      createsUsers &&
      (manifest.company.id < DEMO_COMPANY_ID_MIN || manifest.company.id > DEMO_COMPANY_ID_MAX)
    ) {
      warnings.push(
        t("tour.demo_company_outside_band", {
          id: manifest.company.id,
          min: DEMO_COMPANY_ID_MIN,
          max: DEMO_COMPANY_ID_MAX,
        }),
      );
    }
    const known = new Set(manifest.users.map(user => user.username.trim()));
    const unknownOwners = [
      ...new Set(rows.map(row => row.ownerName).filter(owner => owner !== "" && !known.has(owner))),
    ];
    if (createsUsers && unknownOwners.length > 0) {
      // Not fatal: the owner may already exist from an earlier import. Resolution happens against
      // the server at run time, and an owner that truly matches nobody fails its own row there.
      warnings.push(t("tour.demo_owners_not_in_manifest", { owners: unknownOwners.join(", ") }));
    }

    setBundle({ manifest, rows, missingFiles, warnings });
  };

  const updateRow = (index: number, update: Partial<SessionRow>) => {
    setBundle(current =>
      current
        ? {
            ...current,
            rows: current.rows.map((row, at) => (at === index ? { ...row, ...update } : row)),
          }
        : current,
    );
  };

  /** Uploads the given rows with a fixed number in flight, creating or updating as appropriate. */
  const uploadSessions = async (
    indices: number[],
    rows: SessionRow[],
    owners: Map<string, User>,
    existing: Map<string, string>,
  ) => {
    const queue = [...indices];
    const worker = async () => {
      for (;;) {
        const index = queue.shift();
        if (index === undefined) return;
        const row = rows[index];
        const owner = owners.get(row.ownerName.toLowerCase());
        if (!owner) {
          updateRow(index, {
            status: "failed",
            error: t("tour.demo_owner_unresolved", { owner: row.ownerName || "—" }),
          });
          continue;
        }
        updateRow(index, { status: "uploading", error: null });
        try {
          // Read inside the worker, not up front: a demo's analyses are half a megabyte each, and
          // holding all of them in memory at once is the one way this falls over.
          const analysis: unknown = JSON.parse(await row.file.text());
          const already = existing.get(sessionKey(owner.id, analysisStartTimestamp(analysis)));
          if (already) {
            await admin_patch_recording(already, {
              name: row.name,
              analysis_result: analysis as Record<string, never>,
            });
            updateRow(index, { status: "updated", error: null });
          } else {
            await admin_create_recording({
              name: row.name,
              user_id: owner.id,
              include_in_processes: row.includeInProcesses,
              analysis_result: analysis as Record<string, never>,
            });
            updateRow(index, { status: "created", error: null });
          }
        } catch (err: unknown) {
          updateRow(index, {
            status: "failed",
            error:
              err instanceof SyntaxError
                ? t("tour.demo_file_unreadable")
                : formatApiRequestError(err, t),
          });
        }
      }
    };
    await Promise.all(
      Array.from({ length: Math.min(UPLOAD_CONCURRENCY, queue.length) }, () => worker()),
    );
  };

  const run = async (only?: "failed") => {
    if (!bundle) return;
    setBusy(true);
    setRunError(null);
    try {
      // People first: the sessions need their ids. Everything is judged before anything is
      // created — a person already seeded into this company is skipped, and a username that
      // belongs to anyone else stops the import with nothing written, since silently attaching
      // sessions to a real account would be the worst possible reading of "skip".
      if (bundle.manifest.users.length > 0 && only !== "failed") {
        const { company } = bundle.manifest;
        const [users, companies, seededIds] = await Promise.all([
          get_users(),
          get_companies(),
          admin_list_seeded_user_ids(),
        ]);
        const seeded = new Set(seededIds);
        const byName = new Map(users.items.map(user => [user.username, user]));
        const skipped: string[] = [];
        const taken: string[] = [];
        const toCreate = bundle.manifest.users.filter(person => {
          const existing = byName.get(person.username.trim());
          if (!existing) return true;
          (seeded.has(existing.id) && existing.company_id === company.id ? skipped : taken).push(
            existing.username,
          );
          return false;
        });
        if (taken.length > 0) {
          throw new Error(t("tour.demo_usernames_taken", { users: taken.join(", ") }));
        }
        setSkippedUsers(skipped);

        if (!companies.items.some(existing => existing.id === company.id)) {
          await create_company({ id: company.id, name: company.name, allow_password_login: true });
        }
        const created: SeededCredential[] = [];
        for (const person of toCreate) {
          const password = generatePassword();
          const user = await create_user({
            username: person.username.trim(),
            first_name: person.first_name ?? null,
            last_name: person.last_name ?? null,
            email: person.email ?? null,
            password,
            company_id: company.id,
            seeded: true,
          });
          if (person.permissions) {
            await set_user_permissions(user.id, person.permissions);
          }
          created.push({ user_id: user.id, username: user.username, password });
        }
        setCredentials(created.length > 0 ? created : null);
      }

      if (bundle.rows.length > 0) {
        const users = await get_users();
        const owners = new Map(users.items.map(user => [user.username.toLowerCase(), user]));
        const ownerIds = [
          ...new Set(
            bundle.rows
              .map(row => owners.get(row.ownerName.toLowerCase())?.id)
              .filter((id): id is string => !!id),
          ),
        ];
        // What those people already have, so a second import updates rather than collides.
        const existing = new Map<string, string>();
        const { recordings } = await get_all_recordings({ user_ids: ownerIds });
        for (const recording of recordings) {
          existing.set(`${recording.user_id}|${recording.relative_path_on_disk}`, recording.id);
        }

        const indices = bundle.rows
          .map((_, index) => index)
          .filter(index =>
            only === "failed"
              ? bundle.rows[index].status === "failed"
              : bundle.rows[index].status !== "created" && bundle.rows[index].status !== "updated",
          );
        await uploadSessions(indices, bundle.rows, owners, existing);
      }
      onImported();
    } catch (err: unknown) {
      setRunError(formatApiRequestError(err, t));
    } finally {
      setBusy(false);
    }
  };

  const statusIcon = (status: RowStatus) => {
    const [Icon, color] = STATUS_MARKS[status];
    return (
      <Icon
        className={clsx("h-4 w-4 shrink-0", color, status === "uploading" && "animate-spin")}
        aria-hidden="true"
      />
    );
  };

  return (
    <section className="space-y-4 rounded-xl border border-outline bg-surface p-5 shadow-sm">
      <div>
        <h2 className="text-lg font-semibold text-text">{t("tour.demo_import")}</h2>
        <p className="mt-1 text-sm text-text-dim">{t("tour.demo_import_description")}</p>
      </div>

      <div
        onDragOver={event => {
          event.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={event => {
          event.preventDefault();
          setDragging(false);
          void filesFromDrop(event.dataTransfer).then(acceptFiles);
        }}
        className={clsx(
          "flex flex-col items-center gap-2 rounded-xl border border-dashed px-4 py-8 text-center transition",
          dragging ? "border-info bg-info/5" : "border-outline bg-background-alt",
        )}
      >
        <FolderOpen className="h-6 w-6 text-text-dim" aria-hidden="true" />
        <p className="text-sm text-text">{t("tour.demo_drop_folder")}</p>
        <button
          type="button"
          onClick={() => folderInput.current?.click()}
          disabled={busy}
          className="text-sm text-primary transition hover:text-primary/80"
        >
          {t("tour.demo_choose_folder")}
        </button>
        <input
          ref={folderInput}
          type="file"
          multiple
          // Picking a directory rather than its contents, for the people who would rather click.
          {...{ webkitdirectory: "" }}
          onChange={event => void acceptFiles(Array.from(event.target.files ?? []))}
          className="hidden"
        />
      </div>

      {parseError && (
        <div className="rounded-xl border border-danger/30 bg-danger/10 px-4 py-3 text-sm text-danger">
          {parseError}
        </div>
      )}

      {bundle && (
        <div className="space-y-3">
          <div className="rounded-xl border border-outline bg-muted-surface px-4 py-3 text-sm text-text">
            <div className="font-medium">
              {bundle.manifest.company.name} (#{bundle.manifest.company.id})
            </div>
            <div className="text-text-dim">
              {t("tour.demo_bundle_summary", {
                users: bundle.manifest.users.length,
                sessions: bundle.rows.length,
              })}
            </div>
          </div>

          {bundle.warnings
            .concat(
              bundle.missingFiles.length > 0
                ? [
                    t("tour.demo_files_missing", {
                      count: bundle.missingFiles.length,
                      files: bundle.missingFiles.slice(0, 5).join(", "),
                    }),
                  ]
                : [],
            )
            .map(warning => (
              <div
                key={warning}
                className="rounded-xl border border-warning/30 bg-warning/10 px-4 py-3 text-sm text-warning-strong"
              >
                {warning}
              </div>
            ))}

          {bundle.rows.length > 0 && (
            <>
              <div className="flex flex-wrap items-center gap-3 text-sm text-text-dim">
                <span>
                  {t("tour.demo_session_progress", { done: counts.done, total: counts.total })}
                </span>
                {counts.failed > 0 && (
                  <span className="text-danger">
                    {t("tour.demo_failed_count", { count: counts.failed })}
                  </span>
                )}
              </div>
              <div className="max-h-80 overflow-y-auto rounded-xl border border-outline">
                {bundle.rows.map((row, index) => (
                  <div
                    key={`${row.file.name}-${index}`}
                    className="flex items-center gap-3 border-b border-outline px-4 py-2 text-sm last:border-b-0"
                  >
                    {statusIcon(row.status)}
                    <span className="min-w-0 flex-1 truncate text-text" title={row.file.name}>
                      {row.name}
                    </span>
                    <span className="shrink-0 text-xs text-text-dim">
                      {row.ownerName || t("tour.demo_no_owner")}
                    </span>
                    {row.error && (
                      <span
                        className="max-w-[16rem] shrink-0 truncate text-xs text-danger"
                        title={row.error}
                      >
                        {row.error}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </>
          )}

          <div className="flex flex-wrap items-center gap-3">
            <Button
              color="primary"
              onClick={() => void run()}
              disabled={busy}
              loading={busy}
              className="rounded-xl"
            >
              <Upload className="h-4 w-4" aria-hidden="true" />
              {t("tour.demo_import_action")}
            </Button>
            {counts.failed > 0 && !busy && (
              <Button color="secondary" onClick={() => void run("failed")} className="rounded-xl">
                {t("tour.demo_retry_failed")}
              </Button>
            )}
          </div>

          {skippedUsers.length > 0 && (
            <p className="text-sm text-text-dim">
              {t("tour.demo_users_skipped", {
                count: skippedUsers.length,
                users: skippedUsers.slice(0, 5).join(", "),
              })}
            </p>
          )}
        </div>
      )}

      {runError && (
        <div className="rounded-xl border border-danger/30 bg-danger/10 px-4 py-3 text-sm text-danger">
          {runError}
        </div>
      )}

      {credentials && credentials.length > 0 && <Credentials credentials={credentials} />}
    </section>
  );
};

const credentialsCsv = (credentials: SeededCredential[]): string =>
  ["username,password,user_id"]
    .concat(
      credentials.map(credential =>
        [credential.username, credential.password, credential.user_id].join(","),
      ),
    )
    .join("\n");

const Credentials = ({ credentials }: { credentials: SeededCredential[] }) => {
  const { t } = useTranslation();
  const download = () => {
    const url = URL.createObjectURL(new Blob([credentialsCsv(credentials)], { type: "text/csv" }));
    const link = document.createElement("a");
    link.href = url;
    link.download = `demo-users-${Date.now()}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-3 rounded-xl border border-success/30 bg-success/5 p-4">
      <h3 className="font-semibold text-text">{t("tour.demo_user_credentials")}</h3>
      <p className="text-sm text-warning-strong">{t("tour.demo_user_credentials_warning")}</p>
      <Button color="secondary" onClick={download} className="rounded-xl">
        {t("tour.download_csv")}
      </Button>
      <div className="max-h-56 overflow-y-auto rounded-xl border border-outline bg-surface">
        <table className="w-full text-left text-sm">
          <thead className="border-b border-outline text-text-dim">
            <tr>
              <th className="px-4 py-2 font-medium">{t("username")}</th>
              <th className="px-4 py-2 font-medium">{t("password")}</th>
            </tr>
          </thead>
          <tbody>
            {credentials.map(credential => (
              <tr key={credential.user_id} className="border-b border-outline last:border-b-0">
                <td className="px-4 py-2 text-text">{credential.username}</td>
                <td className="px-4 py-2 font-mono text-text">{credential.password}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default DemoImport;
